import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import useScript from "react-script-hook";

import style from "./wasm-visual-editor-view.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import {
    compareEditorVersionDtos,
    compareEditorVersions,
    deduceEditorVersion,
} from "components/workflows/WorkflowUtil";
import { Authority } from "domain/roles";
import { ActionType, Workflow, WorkflowEntityType } from "domain/workflows";
import { getArthurUrl, getGlobalUrl, getLaurelUrl, getOliverUrl, getStanUrl } from "services/login/endpointRepository";
import { userSessionService } from "services/user/UserSessionService";
import { ManifestWorkflowEditor, Profile } from "services/workflows/WorkflowService";
import { StoreState } from "store";

const mapState = (state: StoreState) => ({
    tenantUuid: state.userReducer.user?.tenantUuid,
});

// Qt version updated from 6.3 to 6.8 in editor version BMDE 5.7.0 and BDE 7.15.0
const BMDE_DEDUCED_LATEST_EDITOR_VERSION = "5.7.0";
const BDE_DEDUCED_LATEST_EDITOR_VERSION = "7.15.0";

interface Props {
    profile: Profile;
    version: string;
    workflow?: Workflow;
    workflowEditors: ManifestWorkflowEditor[];
    setWorkflow?: (workflow: Workflow) => void;
    workflowName?: string;
    uuid?: string;
    actionType: ActionType;
    entityType: WorkflowEntityType;
    bmdeDiagnostics?: string;
    preventNameChange: boolean;
    setHasNewEditorLoaded?: React.Dispatch<React.SetStateAction<boolean>>;
    preview?: boolean;
    restoreVersionId?: string;
}

const connector = connect(mapState, {});
const WasmVisualEditorView = (props: ConnectedProps<typeof connector> & Props): JSX.Element => {
    const [loading, setLoading] = React.useState<boolean>(true);
    const globalUrl = getGlobalUrl();
    const getEditorUrl = (editor: ManifestWorkflowEditor) => {
        const split = editor.url.split("workflow-editors");
        return window.location.hostname == "localhost"
            ? globalUrl + "/workflow-editors" + split[1]
            : "workflow-editors" + split[1];
    };

    const pickWorkflowEditor = (profile: Profile, version?: string): string => {
        const productProfile = profile === Profile.BMDE || profile === Profile.BMDE_DIAGNOSTIC ? Profile.BMDE : profile;
        const sorted = props.workflowEditors
            .filter((editor) => editor.profile === productProfile && editor.editorGeneration == "V2")
            .sort(compareEditorVersionDtos);
        if (version) {
            const editor = sorted.find((editor) => editor.version === version);
            if (editor) {
                return getEditorUrl(editor);
            } else {
                const versionSplit = version.split(".");
                if (versionSplit.length > 3) {
                    versionSplit.pop();
                    const editor = sorted.find((editor) => editor.version === versionSplit.join("."));
                    if (editor) {
                        return getEditorUrl(editor);
                    }
                }
            }
        }

        return getEditorUrl(sorted[0]);
    };

    const url = pickWorkflowEditor(props.profile, props.version);
    const cacheBuster = `?v=${props.version}&t=${Date.now()}`;

    const [bootstrapEditorScriptLoading, bootstrapEditorScriptLoadingError] = useScript({
        src: url + "editor.js" + cacheBuster,
        checkForExisting: false,
    });
    const [bootstrapQtLoaderScriptLoading, bootstrapQtLoaderScriptLoadingError] = useScript({
        src: url + "qtloader.js" + cacheBuster,
        checkForExisting: false,
    });

    if (bootstrapEditorScriptLoadingError) {
        console.error("Failed to load editor source: " + bootstrapEditorScriptLoadingError);
        return <></>;
    }

    if (bootstrapQtLoaderScriptLoadingError) {
        console.error("Failed to load qtloader source: " + bootstrapQtLoaderScriptLoadingError);
        return <></>;
    }

    const canvasElementId = "screen";

    const initEditor = async () => {
        const deducedVersion = deduceEditorVersion(props.version);
        let newEntryFunction = false;
        const oliverUrl = getOliverUrl();
        const parameters = [
            "httpApiBaseUrl=" + oliverUrl, // To be kept for backwards compatibility.
            "oliverApiBaseUrl=" + oliverUrl,
            "laurelApiBaseUrl=" + getLaurelUrl(),
            "arthurApiBaseUrl=" + getArthurUrl(),
            "stanApiBaseUrl=" + getStanUrl(),
            "bmpBaseUrl=" + globalUrl,
        ];
        if (props.workflowName) {
            parameters.push("newDocumentName=" + props.workflowName);
        }
        if (props.uuid) {
            parameters.push("docCloudUuid=" + props.uuid);
        }
        if (props.actionType) {
            parameters.push("actionType=" + props.actionType);
        }
        if (props.bmdeDiagnostics == Profile.BMDE_DIAGNOSTIC) {
            parameters.push("newDocumentProfile=4");
        }
        if (props.preventNameChange) {
            parameters.push("preventNameChange=" + props.preventNameChange);
        }
        if (props.entityType === WorkflowEntityType.TEMPLATE) {
            parameters.push("template=true");
        } else if (props.entityType === WorkflowEntityType.SUB_WORKFLOW) {
            parameters.push("subWorkflow=true");
            parameters.push("template=true");
        }
        if (props.preview === true) {
            parameters.push("previewMode=" + props.preview);
        }

        if (props.profile === Profile.BMDE || props.profile === Profile.BMDE_DIAGNOSTIC) {
            newEntryFunction = compareEditorVersions(BMDE_DEDUCED_LATEST_EDITOR_VERSION, deducedVersion) >= 0;
        }

        if (props.profile === Profile.BDE) {
            newEntryFunction = compareEditorVersions(BDE_DEDUCED_LATEST_EDITOR_VERSION, deducedVersion) >= 0;
        }

        if (props.profile === Profile.LUN || props.profile === Profile.BMP) {
            newEntryFunction = true;
        }

        if (
            props.profile === Profile.BMP &&
            (props.entityType === WorkflowEntityType.TEMPLATE
                ? userSessionService.userHasAllAuthorities([Authority.AUTH_WORKFLOW_TEMPLATE_TEST])
                : userSessionService.userHasAllAuthorities([Authority.AUTH_WORKFLOW_TEST]))
        ) {
            parameters.push("testBmpWorkflow=true");
        }

        if (props.restoreVersionId) {
            parameters.push("versionId=" + props.restoreVersionId);
        }

        if (props.profile === Profile.BMP) {
            parameters.push("enableReportQuery=true");
        }

        if (
            props.profile === Profile.BMP &&
            (props.entityType === WorkflowEntityType.WORKFLOW
                ? userSessionService.userHasAllAuthorities([Authority.AUTH_WORKFLOW_SECURED])
                : userSessionService.userHasAllAuthorities([Authority.AUTH_WORKFLOW_TEMPLATE_SECURED]))
        ) {
            parameters.push("securedWorkflow=true");
        }

        if (
            props.profile === Profile.BMP &&
            props.entityType === WorkflowEntityType.TEMPLATE &&
            userSessionService.userHasAllAuthorities([Authority.AUTH_WORKFLOW_TEMPLATE_SCHEDULE])
        ) {
            parameters.push("scheduleWorkflow=true");
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.wasmInstance = await qtLoad({
            arguments: parameters,
            qt: {
                onLoaded: () => {
                    setLoading(false);
                    props.setHasNewEditorLoaded && props.setHasNewEditorLoaded(true);
                },
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                entryFunction: newEntryFunction === true ? window.editor_entry : window.createQtAppInstance,
                containerElements: [document.getElementById(canvasElementId)],
            },
        });
    };

    React.useEffect(() => {
        if (
            bootstrapEditorScriptLoading ||
            bootstrapEditorScriptLoadingError ||
            bootstrapQtLoaderScriptLoading ||
            bootstrapQtLoaderScriptLoadingError ||
            typeof props.tenantUuid === "undefined"
        ) {
            return;
        }

        initEditor();
    }, [bootstrapEditorScriptLoading, bootstrapQtLoaderScriptLoading]);

    return (
        <div id={canvasElementId} className={style.editorCanvas}>
            {loading && <LoadingIndicator />}
        </div>
    );
};

export default connector(WasmVisualEditorView);
